import React, {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
} from 'react';

export type BreadcrumbItem = {
    id: string;
    label?: string;
    href?: string;
};

type BreadcrumbContextType = {
    setCrumb: (crumb: BreadcrumbItem) => void;
    removeCrumb: (id: string) => void;
    orderedCrumbs: Array<BreadcrumbItem>;
};

const BreadcrumbContext = createContext<BreadcrumbContextType | undefined>(
    undefined,
);

export const BreadcrumbProvider = ({
    children,
}: React.PropsWithChildren<unknown>) => {
    const [crumbs, setCrumbs] = useState<Map<string, BreadcrumbItem>>(
        new Map(),
    );

    const setCrumb = useCallback((crumb: BreadcrumbItem) => {
        setCrumbs((prev) => {
            const next = new Map(prev);
            next.set(crumb.id, crumb);
            return next;
        });
    }, []);

    const removeCrumb = useCallback((id: string) => {
        setCrumbs((prev) => {
            const next = new Map(prev);
            next.delete(id);
            return next;
        });
    }, []);

    const orderedCrumbs = useMemo(() => Array.from(crumbs.values()), [crumbs]);

    return (
        <BreadcrumbContext.Provider
            value={{
                setCrumb,
                removeCrumb,
                orderedCrumbs,
            }}
        >
            {children}
        </BreadcrumbContext.Provider>
    );
};

export const useBreadcrumbContext = () => {
    const ctx = useContext(BreadcrumbContext);
    if (!ctx) {
        throw new Error(
            'useBreadcrumbContext must be used inside BreadcrumbProvider',
        );
    }
    return ctx;
};
